import type { ReactNode } from 'react';

import { useTheme } from '@mui/material';

import { gql } from '@graphql/__generated__';
import type { ActivityTitleFragment } from '@graphql/__generated__/graphql';
import { ProfileInquiryAssignmentTitle } from '@modules/_shared/components/feed/ProfileInquiryAssignmentTitle';
import { isProfileInquiryAssignment } from '@modules/_shared/components/feed/utils/activitySource';
import { NameLink } from '@modules/_shared/components/feed-v2/components/NameLink';
import { StageChip } from '@modules/_shared/components/feed-v2/components/StageChip';
import {
  isAppraisalRequest,
  isProspectionMapAssignment,
  isProspectionMapOutreach,
} from '@modules/_shared/components/feed-v2/utils/activityHelpers';
import {
  parseLeadStatusAgentExtraData,
  resolveStageLabel,
} from '@modules/_shared/components/feed-v2/utils/leadStatusAgentParser';
import { parseModuleActivityModuleId } from '@modules/_shared/components/feed-v2/utils/moduleActivityParser';
import {
  getOnboardingStatusHeadline,
  parseProfileVisibilityPublished,
} from '@modules/_shared/components/feed-v2/utils/onboardingActivityParser';
import { getLotStatusLabel } from '@modules/_shared/components/LotCardInfo';
import { useTranslationWithComponents } from '@modules/_shared/hooks/useTranslationWithComponents';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { userCanViewLeadDetails } from '@modules/_shared/utils/listingPermissions';
import {
  coerceCurrency,
  parseChangeExtraData,
} from '@modules/_shared/utils/lotChangeExtraData';
import { useLocale } from '@shared/hooks/locale';
import { formatPrice } from '@shared/utils/format-price';
import { getModuleLabel } from '@shared/utils/module-label';

export const ACTIVITY_TITLE_FRAGMENT = gql(/* GraphQL */ `
  fragment ActivityTitle on activities {
    id
    activity_type
    success
    done
    created_by
    buyer_lead_id
    enquiry_id
    lot_id
    extra_data
    creator {
      ...UserAvatar
    }
    assignee {
      ...UserAvatar
    }
    email_message {
      to
      cc
    }
    user {
      ...NameLink
    }
    activities_target_users(where: { is_owner: { _eq: false } }) {
      name
      email
      target_user_id
      user {
        ...NameLink
      }
    }
    lead {
      created_by
      origin_enquiry_id
      source {
        id
        name
      }
      lead_agents {
        user_id
        claimed_at
        source
      }
      profile_contact {
        id
        full_name
        first_name
        last_name
        profile_image_url
      }
      contact {
        id
        full_name
        first_name
        last_name
        emails {
          email
        }
      }
    }
  }
`);

type ActivityTitleProps = {
  activity: ActivityTitleFragment;
  count: number;
  allDone: boolean;
};

const buildDescription = (
  actor: ReactNode,
  action: string,
  target: ReactNode | null,
  suffix: string,
  attribution?: ReactNode,
) => (
  <>
    {actor} {action}
    {target != null && <span> {target}</span>}
    {suffix}
    {attribution != null && <span> {attribution}</span>}
  </>
);

export const ActivityTitle = ({
  activity,
  count = 1,
  allDone = false,
}: ActivityTitleProps) => {
  const { me } = useAppData();
  const { text } = useTheme();
  const { t, locale, language } = useLocale();
  const tWithComponents = useTranslationWithComponents();
  const creatorId = activity.creator?.id;
  const assigneeId = activity.assignee?.id;

  const obfuscateLeadDetails =
    activity.lead != null ? !userCanViewLeadDetails(me, activity.lead) : false;

  const creator = (
    <NameLink
      user={activity.creator ?? null}
      obfuscate={
        (activity.activity_type !== 'assignment' && obfuscateLeadDetails) ||
        (isAppraisalRequest(activity) && obfuscateLeadDetails)
      }
    />
  );

  // The assignee is internal staff by convention, but `assigned_to` is an
  // unconstrained FK to `users`, which also stores lead contacts. Only skip
  // obfuscation once we know this row is not the lead's own contact.
  const isInternalAssignee = (userId: string | undefined) =>
    userId != null &&
    userId === assigneeId &&
    assigneeId !== activity.lead?.contact?.id;

  const assignee =
    activity.assignee != null ? (
      <NameLink
        user={activity.assignee}
        obfuscate={
          obfuscateLeadDetails && !isInternalAssignee(activity.assignee.id)
        }
      />
    ) : null;
  const getResolvedUser = (
    p: (typeof activity.activities_target_users)[number],
  ) => p.user ?? null;

  const allTargetUsers = (activity.activities_target_users ?? []).filter(
    Boolean,
  );
  const selfParticipant = allTargetUsers.find(p => {
    const matchedId = getResolvedUser(p)?.id;
    return matchedId === creatorId || matchedId === assigneeId;
  });

  const filteredParticipants = allTargetUsers.filter(p => {
    const matchedId = getResolvedUser(p)?.id;
    if (matchedId == null) {
      return true;
    }

    return matchedId !== creatorId && matchedId !== assigneeId;
  });

  if (selfParticipant) {
    filteredParticipants.push(selfParticipant);
  }

  const displayParticipants = filteredParticipants;
  const targetUsersLink =
    displayParticipants.length > 0 ? (
      <>
        {displayParticipants.map((participant, i) => {
          const resolvedUser = getResolvedUser(participant);
          return (
            <span key={participant.target_user_id ?? participant.email ?? i}>
              {i > 0 && ', '}
              {resolvedUser != null ? (
                // selfParticipant is filtered out above then pushed back, so the
                // assignee can land here too and would print twice on one line,
                // once full and once truncated. Same rule as the assignee slot.
                // (The creator can too — that predates this change.)
                <NameLink
                  user={resolvedUser}
                  obfuscate={
                    obfuscateLeadDetails && !isInternalAssignee(resolvedUser.id)
                  }
                />
              ) : (
                <span style={{ fontWeight: 'bold' }}>{participant.name}</span>
              )}
            </span>
          );
        })}
      </>
    ) : null;

  const profileContact =
    isProfileInquiryAssignment(activity) &&
    !obfuscateLeadDetails &&
    activity.lead?.profile_contact != null
      ? activity.lead.profile_contact
      : null;
  const contactLink =
    profileContact?.id != null ? (
      <NameLink
        user={{ ...profileContact, id: profileContact.id }}
        link={false}
      />
    ) : activity.lead?.contact != null ? (
      <NameLink user={activity.lead.contact} obfuscate={obfuscateLeadDetails} />
    ) : (
      targetUsersLink
    );
  const times = count > 1 ? ` ${count}x` : '';

  // Common check: assignee exists and is different from creator
  const hasDifferentAssignee = assignee && assigneeId !== creatorId;
  const actorForCompletion = hasDifferentAssignee ? assignee : creator;

  switch (activity.activity_type) {
    case 'assignment':
      if (isProfileInquiryAssignment(activity) && assignee) {
        return (
          <ProfileInquiryAssignmentTitle
            contact={
              (!obfuscateLeadDetails && activity.lead?.contact != null) ||
              profileContact?.id != null
                ? contactLink
                : undefined
            }
            agent={assignee}
            suffix={times}
          />
        );
      }
      if (isAppraisalRequest(activity) && assignee) {
        return buildDescription(
          contactLink || creator,
          `${t('requested an on-site appraisal from')} `,
          assignee,
          times,
        );
      }
      if (isProspectionMapAssignment(activity)) {
        const agent = assignee ?? creator;
        if (contactLink != null) {
          return (
            <>
              {tWithComponents(
                '{{contact}} accepted a contact request from {{agent}} on the prospection map',
                { contact: contactLink, agent },
              )}
              {times}
            </>
          );
        }
        return (
          <>
            {tWithComponents(
              'A property owner accepted a contact request from {{agent}} on the prospection map',
              { agent },
            )}
            {times}
          </>
        );
      }
      if (assignee) {
        return buildDescription(
          creator,
          `${t('assigned this lead to')} `,
          assignee,
          times,
        );
      }
      return buildDescription(
        creator,
        t('updated the assignment'),
        null,
        times,
      );

    case 'requalification':
      return buildDescription(creator, t('requalified this lead'), null, times);

    case 'note':
      return buildDescription(
        creator,
        targetUsersLink ? t('wrote a note about') : t('wrote a note'),
        targetUsersLink,
        times,
      );

    case 'origination': {
      if (activity.enquiry_id != null) {
        return buildDescription(creator, t('created an enquiry'), null, times);
      }
      const originationLabel = activity.buyer_lead_id
        ? t('created a new buyer lead')
        : activity.lot_id
        ? t('created this listing')
        : t('created this lead');
      return buildDescription(creator, originationLabel, null, times);
    }

    case 'visit':
      if (activity.done && activity.success) {
        return buildDescription(
          actorForCompletion,
          targetUsersLink
            ? t('completed a visit with')
            : t('completed a visit'),
          targetUsersLink,
          times,
          hasDifferentAssignee ? (
            <>
              {t('organised by')} {creator}
            </>
          ) : undefined,
        );
      }
      if (activity.done && !activity.success) {
        return buildDescription(
          actorForCompletion,
          targetUsersLink
            ? t('cancelled a visit with')
            : t('cancelled a visit'),
          targetUsersLink,
          times,
          hasDifferentAssignee ? (
            <>
              {t('organised by')} {creator}
            </>
          ) : undefined,
        );
      }
      // Pending visit
      if (hasDifferentAssignee) {
        const target = targetUsersLink ? (
          <>
            {assignee} {t('with')} {targetUsersLink}
          </>
        ) : (
          assignee
        );
        return buildDescription(
          creator,
          `${t('organised a visit for')} `,
          target,
          times,
        );
      }
      return buildDescription(
        creator,
        targetUsersLink ? t('organised a visit with') : t('organised a visit'),
        targetUsersLink,
        times,
      );

    case 'task':
      if (allDone) {
        return buildDescription(
          actorForCompletion,
          t('completed a task'),
          null,
          times,
          hasDifferentAssignee ? (
            <>
              {t('assigned by')} {creator}
            </>
          ) : undefined,
        );
      }
      if (hasDifferentAssignee) {
        return buildDescription(
          creator,
          `${t('assigned a task to')} `,
          assignee,
          times,
        );
      }
      return buildDescription(creator, t('created a task'), null, times);

    case 'call':
      if (allDone) {
        const action = activity.success ? t('called') : t('tried to call');
        return buildDescription(
          actorForCompletion,
          action,
          targetUsersLink,
          times,
          hasDifferentAssignee ? (
            <>
              {t('organised by')} {creator}
            </>
          ) : undefined,
        );
      }
      // Pending call
      if (hasDifferentAssignee) {
        const target = targetUsersLink ? (
          <>
            {assignee} {t('with')} {targetUsersLink}
          </>
        ) : (
          assignee
        );
        return buildDescription(
          creator,
          `${t('scheduled a call for')} `,
          target,
          times,
        );
      }
      return buildDescription(
        creator,
        targetUsersLink ? t('scheduled a call with') : t('scheduled a call'),
        targetUsersLink,
        times,
      );

    case 'email': {
      if (isProspectionMapOutreach(activity)) {
        if (contactLink != null) {
          return (
            <>
              {tWithComponents(
                '{{agent}} sent a contact request to {{contact}} from the prospection map',
                { agent: creator, contact: contactLink },
              )}
              {times}
            </>
          );
        }
        return (
          <>
            {creator} {t('sent a contact request from the prospection map')}
            {times}
          </>
        );
      }
      // Combine TO and CC recipients for the description
      const toRecipients = activity.email_message?.to ?? [];
      const ccRecipients = activity.email_message?.cc ?? [];
      const allRecipients = [...toRecipients, ...ccRecipients];

      const recipientElements: ReactNode[] = allRecipients
        .map(
          (
            recipient: { name?: string | null; email?: string | null } | null,
            idx: number,
          ) => {
            const name = recipient?.name;
            const email = recipient?.email;

            if (name == null && email == null) {
              return null;
            }

            const displayName = (name || email) as string;

            // Try to resolve a user: lead contact, or target_user by email
            const matchedParticipant = displayParticipants.find(
              p => p.email === email,
            );
            const resolvedUser = getResolvedUser(
              matchedParticipant ?? {
                user: null,
                name: null,
                email: null,
                target_user_id: null,
              },
            );
            const isContact =
              email != null &&
              activity.lead?.contact?.emails.some(
                cEmail => cEmail.email === email,
              );
            const linkedUser = isContact
              ? activity.lead?.contact
              : resolvedUser;

            const element =
              linkedUser != null ? (
                <NameLink key={idx} user={linkedUser} />
              ) : (
                <span key={idx} style={{ fontWeight: text.font.bold }}>
                  {displayName}
                </span>
              );

            return element;
          },
        )
        .filter(Boolean);

      // Join recipients with commas
      const recipientLink =
        recipientElements.length > 0 ? (
          <>
            {recipientElements.map((el, idx) => (
              <span key={idx}>
                {el}
                {idx < recipientElements.length - 1 ? ', ' : ''}
              </span>
            ))}
          </>
        ) : null;

      return buildDescription(
        creator,
        recipientLink ? t('sent an email to') : t('sent an email'),
        recipientLink,
        times,
      );
    }

    case 'create_user': {
      const createdUser =
        activity.user != null ? <NameLink user={activity.user} /> : null;
      return buildDescription(creator, t('created user'), createdUser, times);
    }

    case 'agent_opt_in': {
      return buildDescription(
        creator,
        t('opted into the agent funnel'),
        null,
        times,
      );
    }

    case 'agent_quiz_completed': {
      return buildDescription(
        creator,
        t('completed the agent quiz'),
        null,
        times,
      );
    }

    case 'lead_status_agent': {
      const extra = parseLeadStatusAgentExtraData(activity.extra_data);
      const toLabel = resolveStageLabel(extra?.stage.to, language);
      if (extra == null || toLabel == null) {
        return buildDescription(
          creator,
          t('updated the pipeline stage'),
          null,
          times,
        );
      }

      // Render the stages as coloured pills (muted origin → emphasised
      // destination) so the new pipeline position stands out in the feed.
      const fromChip = (
        <StageChip
          stage={extra.stage.from}
          language={language}
          variant="from"
        />
      );
      const toChip = (
        <StageChip stage={extra.stage.to} language={language} variant="to" />
      );

      // created_by is the agent whose pipeline moved, so creator is normally
      // set and names the agent the change concerns. This passive headline is
      // a defensive fallback for the rare case the creator is unreadable.
      if (activity.creator == null) {
        return (
          <>
            {tWithComponents('Status changed from {{from}} to {{to}}', {
              from: fromChip,
              to: toChip,
            })}
            {times}
          </>
        );
      }

      return (
        <>
          {creator}{' '}
          {tWithComponents('changed the status from {{from}} to {{to}}', {
            from: fromChip,
            to: toChip,
          })}
          {times}
        </>
      );
    }

    case 'module_granted':
    case 'module_revoked': {
      const moduleId = parseModuleActivityModuleId(activity.extra_data);
      const moduleName = (
        <span style={{ fontWeight: text.font.medium }}>
          {moduleId != null ? getModuleLabel(moduleId, t) : t('Unknown')}
        </span>
      );
      const targetUser = <NameLink user={activity.user ?? null} />;
      const granted = activity.activity_type === 'module_granted';
      if (activity.creator == null) {
        return (
          <>
            {granted
              ? tWithComponents('{{module}} granted to {{user}}', {
                  module: moduleName,
                  user: targetUser,
                })
              : tWithComponents('{{module}} removed from {{user}}', {
                  module: moduleName,
                  user: targetUser,
                })}
            {times}
          </>
        );
      }
      return (
        <>
          {granted
            ? tWithComponents('{{creator}} granted {{module}} to {{user}}', {
                creator,
                module: moduleName,
                user: targetUser,
              })
            : tWithComponents('{{creator}} removed {{module}} from {{user}}', {
                creator,
                module: moduleName,
                user: targetUser,
              })}
          {times}
        </>
      );
    }

    // Trigger-written, readable by tenant admins only (like module grants).
    // created_by is whoever made the change: the agent for a submission, the
    // admin for an approval or a publication.
    case 'onboarding_status_changed': {
      const user = <NameLink user={activity.user ?? null} />;
      const headline = getOnboardingStatusHeadline(activity.extra_data);
      if (headline === 'opened') {
        return (
          <>
            {tWithComponents('Onboarding opened for {{user}}', { user })}
            {times}
          </>
        );
      }
      if (headline === 'submitted') {
        return (
          <>
            {tWithComponents('{{user}} submitted their profile for review', {
              user,
            })}
            {times}
          </>
        );
      }
      // A claimed public agency is approved on submit, with the agent as the
      // actor: not a review, so not "<agent> approved the profile of <agent>".
      if (
        headline === 'approved' &&
        activity.creator != null &&
        activity.creator.id === activity.user?.id
      ) {
        return (
          <>
            {tWithComponents('Profile of {{user}} approved automatically', {
              user,
            })}
            {times}
          </>
        );
      }
      if (headline === 'approved') {
        return (
          <>
            {activity.creator == null
              ? tWithComponents('Profile of {{user}} approved', { user })
              : tWithComponents(
                  '{{creator}} approved the profile of {{user}}',
                  {
                    creator,
                    user,
                  },
                )}
            {times}
          </>
        );
      }
      if (headline === 'sent_back') {
        return (
          <>
            {activity.creator == null
              ? tWithComponents('Profile of {{user}} sent back for changes', {
                  user,
                })
              : tWithComponents(
                  '{{creator}} sent the profile of {{user}} back for changes',
                  { creator, user },
                )}
            {times}
          </>
        );
      }
      return (
        <>
          {tWithComponents('Onboarding status of {{user}} changed', { user })}
          {times}
        </>
      );
    }

    case 'agent_profile_visibility_changed': {
      const user = <NameLink user={activity.user ?? null} />;
      const published = parseProfileVisibilityPublished(activity.extra_data);
      if (published == null) {
        return (
          <>
            {tWithComponents('Profile visibility of {{user}} changed', {
              user,
            })}
            {times}
          </>
        );
      }
      if (activity.creator == null) {
        return (
          <>
            {published
              ? tWithComponents('Profile of {{user}} published', { user })
              : tWithComponents('Profile of {{user}} unpublished', { user })}
            {times}
          </>
        );
      }
      if (activity.creator.id === activity.user?.id) {
        return (
          <>
            {published
              ? tWithComponents('{{user}} published their profile', { user })
              : tWithComponents('{{user}} unpublished their profile', { user })}
            {times}
          </>
        );
      }
      return (
        <>
          {published
            ? tWithComponents('{{creator}} published the profile of {{user}}', {
                creator,
                user,
              })
            : tWithComponents(
                '{{creator}} unpublished the profile of {{user}}',
                { creator, user },
              )}
          {times}
        </>
      );
    }

    case 'change': {
      // Change activities are only meaningful in a lot context — without a
      // lot_id we can't render a coherent title, so fall through to the
      // generic copy. Listing feed already scopes the query to lot_id, so in
      // practice this branch always runs with lot_id set.
      if (activity.lot_id == null) {
        return buildDescription(creator, t('performed an action'), null, times);
      }

      const extra = parseChangeExtraData(activity.extra_data);
      if (extra == null) {
        return buildDescription(creator, t('updated the listing'), null, times);
      }

      const bold = (node: ReactNode) => (
        <span style={{ fontWeight: text.font.medium }}>{node}</span>
      );

      const translateOfferType = (value: string | null | undefined) => {
        if (value === 'rent') {
          return t('Rent');
        }
        if (value === 'sell') {
          return t('Sell');
        }
        // OfferTypeDeltaSchema.from is nullable — render an em-dash so the
        // bold span doesn't collapse into an empty "from **** to Sell".
        // Mirrors the status branch's null-from handling above.
        return value ?? '—';
      };

      let title: ReactNode = null;

      if ('auto_review' in extra) {
        // Auto-review activities discriminate on `auto_review` rather than
        // on status.to, because a "held" decision keeps status at
        // pending_review (from === to) and would otherwise fall into the
        // generic status-change line below. Check this branch first.
        const score = extra.auto_review.score;
        if (extra.auto_review.decision === 'approved') {
          title = tWithComponents(
            'auto-approved the FSBO listing (score: {{score}})',
            { score: bold(String(score)) },
          );
        } else if (extra.auto_review.shadow_decision === 'approved') {
          // Shadow mode: auto-approval is switched off, so the bot held a
          // listing it would otherwise have approved. Say so in the headline —
          // this is the row a reviewer should be able to wave through.
          title = tWithComponents(
            'held the FSBO listing for review — would have auto-approved (score: {{score}})',
            { score: bold(String(score)) },
          );
        } else {
          title = tWithComponents(
            'held the FSBO listing for review (score: {{score}})',
            { score: bold(String(score)) },
          );
        }
      } else if ('fsbo_block' in extra) {
        // block_fsbo_user: the advertiser behind the listing was shadow
        // banned. A live listing was taken down to pending_review; one
        // already pending is only annotated (no status delta).
        title =
          extra.status != null && extra.status.from !== extra.status.to
            ? tWithComponents(
                'blocked the advertiser and took the listing down ({{from}} to {{to}})',
                {
                  from: bold(
                    extra.status.from != null
                      ? getLotStatusLabel(extra.status.from, t)
                      : '—',
                  ),
                  to: bold(getLotStatusLabel(extra.status.to, t)),
                },
              )
            : t(
                'blocked the advertiser — this listing stays in pending review and will not go live',
              );
      } else if ('fsbo_unblock' in extra) {
        // unblock_fsbo_users: the account was cleared. The listing stays in
        // pending review until a reviewer approves it by hand.
        title = t(
          'unblocked the advertiser — this listing can be reviewed and approved again',
        );
      } else if ('portal' in extra) {
        const portalName = extra.portal.name ?? t('portal');
        title = extra.portal.live
          ? tWithComponents('published the listing to {{portal}}', {
              portal: bold(portalName),
            })
          : tWithComponents('unpublished the listing from {{portal}}', {
              portal: bold(portalName),
            });
      } else if ('offer_type' in extra) {
        title = tWithComponents(
          'changed the offer type from {{from}} to {{to}}',
          {
            from: bold(translateOfferType(extra.offer_type.from)),
            to: bold(translateOfferType(extra.offer_type.to)),
          },
        );
      } else if ('status' in extra) {
        const fromLabel =
          extra.status.from != null
            ? getLotStatusLabel(extra.status.from, t)
            : '—';
        const toLabel = getLotStatusLabel(extra.status.to, t);
        const fromNode = bold(fromLabel);
        const toNode = bold(toLabel);
        if (extra.status.to === 'pending_review') {
          title = tWithComponents(
            'submitted the FSBO listing for review ({{from}} to {{to}})',
            { from: fromNode, to: toNode },
          );
        } else if (extra.status.to === 'paused' && 'fsbo' in extra) {
          title = tWithComponents(
            'deactivated their FSBO listing ({{from}} to {{to}})',
            { from: fromNode, to: toNode },
          );
        } else if (extra.status.to === 'rejected' && 'fsbo' in extra) {
          title = tWithComponents(
            'rejected the FSBO listing ({{from}} to {{to}})',
            { from: fromNode, to: toNode },
          );
        } else {
          title = tWithComponents(
            'changed the listing status from {{from}} to {{to}}',
            { from: fromNode, to: toNode },
          );
        }
      } else if ('price' in extra) {
        const { field, from, to, currency } = extra.price;
        const increased = from < to;
        const cur = coerceCurrency(currency);
        const fromNode = bold(formatPrice(from, locale, cur));
        const toNode = bold(formatPrice(to, locale, cur));

        switch (field) {
          case 'sale_price':
            title = increased
              ? tWithComponents(
                  'increased the sale price from {{from}} to {{to}}',
                  { from: fromNode, to: toNode },
                )
              : tWithComponents(
                  'decreased the sale price from {{from}} to {{to}}',
                  { from: fromNode, to: toNode },
                );
            break;
          case 'rent_net':
            title = increased
              ? tWithComponents('increased the rent from {{from}} to {{to}}', {
                  from: fromNode,
                  to: toNode,
                })
              : tWithComponents('decreased the rent from {{from}} to {{to}}', {
                  from: fromNode,
                  to: toNode,
                });
            break;
          case 'rent_extra':
            title = increased
              ? tWithComponents(
                  'increased the rent extras from {{from}} to {{to}}',
                  { from: fromNode, to: toNode },
                )
              : tWithComponents(
                  'decreased the rent extras from {{from}} to {{to}}',
                  { from: fromNode, to: toNode },
                );
            break;
        }
      }

      return (
        <>
          {creator} {title ?? t('updated the listing')}
          {times}
        </>
      );
    }

    default:
      return buildDescription(creator, t('performed an action'), null, times);
  }
};
